import { serializeJsonLd, isProfileSearchIndexable } from '../../shared/utils/seo';
import React, { useEffect, useState } from 'react';
import Head from 'next/head';

import { PlusCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Avatar, Badge, Button, Col, Result, Row, Switch, Tabs, Tooltip, message } from 'antd';
import {
  getAvailabilityByUserId,
  getLeagueById,
  getMatchesForPlayer,
  getPublicDocsByIds as getDocsByIds,
  getPrizeByUserId,
  getPrizes,
  getPublicUserById as getUserById,
  getCourts,
} from '../../lib/firestoreAdmin';
import { USERS_COLLECTION } from '../../shared/variables/collections';
import SvgPin from '../../components/Icons/Pin';
import ListDetailMiddleColumn from '../../components/ListDetail/ListDetailMiddleColumn';
import ListDetailRightColumn from '../../components/ListDetail/ListDetailRightColumn';
import WeeklyAvailabilityGrid from '../../components/Profile/WeeklyAvailabilityGrid';
import LeagueSummary from '../../components/Profile/LeagueSummary';
import { findNextMatch } from '../../shared/utils/next-match';
import Prizes from '../../components/Profile/Prizes';
import UserInfo from '../../components/Profile/UserInfo';
import { GetServerSidePropsContext } from 'next';
import { useAppSelector } from '../../store/hooks';
import { selectUser, selectUserData } from '../../store/slices/userSlice';
import { useQuery } from 'react-query';
import { auth } from '../../lib/firebaseAuth';
import { canViewPlayerContact } from '../../shared/utils/public-user';
import { useRouter } from 'next/router';
import BaseHead from '../../components/Base/BaseHead';
import { ROUTE_PLAY } from '../../shared/variables/routes';
import dynamic from 'next/dynamic';
import { EmptyText } from '../../shared/messages/empty-text';
import Matches from '../../components/Profile/Matches';
import { NotificationText } from '../../shared/messages/notification-text';
import VerifiedAccount from '../../components/Base/VerifiedAccount';
import RecentForm from '../../components/Profile/RecentForm';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { convertTitleToUrl } from '../../shared/utils/url';
import { storageImage } from '../../shared/utils/storage-image';
import TabPane = Tabs.TabPane;

const LineChart = dynamic(
  () => {
    return import('../../components/Charts/LineChart');
  },
  { ssr: false },
);
// Takip listesi modalı tıklanınca açılır; Firestore'u kamusal profilin ilk paketine sokmaz.
const FollowListModal = dynamic(() => import('../../components/Profile/FollowListModal'), {
  ssr: false,
});

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const profileId = `${ctx.query?.id}`.split('__')[1];

  if (!profileId) {
    return { notFound: true };
  }

  try {
    const user = await getUserById(profileId);

    if (!user) {
      return { notFound: true };
    }

    // 301 redirect if slug is stale (user changed name)
    const canonicalSlug = convertTitleToUrl('profil', `${user.ad} ${user.soyad}`, user.id).replace(/^\/profil\//, '');
    if (`${ctx.query?.id}` !== canonicalSlug) {
      return { redirect: { destination: `/profil/${canonicalSlug}`, permanent: true } };
    }

    // Parallelized fetches — reduces TTFB significantly.
    // `getAllMatches()` (tüm koleksiyon) yerine yalnızca bu oyuncunun maçları,
    // `getUsers()` (tüm koleksiyon) tamamen kaldırıldı.
    const [prizes, userPrizes, availabilityResult, userMatches, courts, league] =
      await Promise.all([
        getPrizes(),
        getPrizeByUserId(user.id),
        getAvailabilityByUserId(user.id),
        getMatchesForPlayer(user.id),
        getCourts(),
        user.lig_temel_id ? getLeagueById(user.lig_temel_id) : Promise.resolve(null),
      ]);

    const availability = availabilityResult[0] || [];

    const matches = userMatches
      .filter(item => item.mac_durum !== 0 && item.lig_temel_id === user.lig_temel_id)
      .sort((a, b) => b.mac_saati - a.mac_saati);

    const cannotOffers: string[] = [];
    userMatches
      .filter(item => item.mac_durum === 0)
      .forEach(item => {
        if (item.kul_t_id === user.id) {
          cannotOffers.push(item.kul_r_id);
        } else {
          cannotOffers.push(item.kul_t_id);
        }
      });

    // <Matches /> yalnızca maçlarda adı geçen oyuncuları çözmek için `users`
    // kullanıyor; tüm koleksiyon yerine sadece o ID'ler çekiliyor.
    const opponentIds = matches.map(m => (m.kul_t_id === user.id ? m.kul_r_id : m.kul_t_id));
    const users = await getDocsByIds<Users.User>(USERS_COLLECTION, [user.id, ...opponentIds]);

    ctx.res.setHeader('Cache-Control', 'public, s-maxage=1800, stale-while-revalidate=86400');
    return {
      props: {
        user,
        prizes: prizes || [],
        userPrizes: userPrizes || [],
        availability,
        league,
        cannotOffers,
        matches,
        courts: courts || [],
        users,
      },
    };
  } catch (err) {
    // Return 404 for data errors — never redirect public profiles to login.
    // Googlebot and unauthenticated users must be able to view profile pages.
    return { notFound: true };
  }
};

function Profil({
  user,
  prizes,
  userPrizes,
  availability,
  league,
  cannotOffers,
  matches,
  users,
  courts,
}: {
  user: Users.User;
  prizes: Prizes.Prize[];
  userPrizes: Users.Prize[];
  availability: Users.Availability;
  league: Leagues.League | null;
  cannotOffers: string[];
  matches: Matches.Match[];
  users: Users.User[];
  courts: Courts.Court[];
}) {
  const router = useRouter();
  const userData = useAppSelector(selectUserData);
  const viewerAuth = useAppSelector(selectUser);
  const showContact = Boolean(viewerAuth?.uid && viewerAuth.uid === userData?.uid && canViewPlayerContact(userData, user));
  const contact = useQuery<Pick<Users.User, 'eposta' | 'null_tel'>>(
    ['playerContact', viewerAuth?.uid, userData?.lig_temel_id, user.id, user.lig_temel_id],
    async () => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Authentication required');
      const response = await fetch(`/api/player-contact?id=${encodeURIComponent(user.id)}`, {
        headers: { Authorization: `Bearer ${token}` }, cache: 'no-store',
      });
      if (!response.ok) throw new Error('Contact details unavailable');
      return response.json();
    },
    { enabled: showContact, cacheTime: 0, staleTime: 0, retry: false },
  );
  const { t } = useTranslation('profile');

  const isOwnProfile = userData?.id === user.id;
  // Aynı ligdeki oyuncuya teklif kısayolu; uygunluk (doğrulama, ceza, hafta) teklif sayfasında denetlenir.
  const canProposeMatch = !isOwnProfile && !!userData?.lig_temel_id && userData.lig_temel_id === user.lig_temel_id;

  // ── follow state ──────────────────────────────────────────────────────────
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [followerCount, setFollowerCount] = useState(user.followers_count || 0);

  // ── modals ────────────────────────────────────────────────────────────────
  const [followersModalOpen, setFollowersModalOpen] = useState(false);
  const [followingModalOpen, setFollowingModalOpen] = useState(false);

  // Profil değişince (aynı sayfa bileşeni) önceki kişinin takip durumu/sayısı taşınmaz.
  useEffect(() => {
    setFollowing(false);
    setFollowerCount(user.followers_count || 0);
    setFollowersModalOpen(false);
    setFollowingModalOpen(false);
  }, [user.id, user.followers_count]);

  // Check if logged user already follows this profile
  useEffect(() => {
    if (!userData?.id || isOwnProfile) return;
    let active = true;
    import('../../lib/firestoreClient')
      .then(api => api.isFollowing(userData.id, user.id))
      .then(value => active && setFollowing(value))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [userData?.id, user.id, isOwnProfile]);

  const openFollowersModal = () => setFollowersModalOpen(true);
  const openFollowingModal = () => setFollowingModalOpen(true);

  const handleFollow = async () => {
    if (!userData?.id) return;
    setFollowLoading(true);
    try {
      // Firestore yalnız giriş yapmış ziyaretçinin işleminde yüklenir (kamusal profil paketi küçük kalır).
      const { followUser, unfollowUser } = await import('../../lib/firestoreClient');
      if (following) {
        await unfollowUser(userData.id, user.id);
        setFollowing(false);
        setFollowerCount(c => Math.max(0, c - 1));
      } else {
        await followUser(userData.id, user.id);
        setFollowing(true);
        setFollowerCount(c => c + 1);
      }
    } catch {
      message.error(t('followList.actionError'));
    } finally {
      setFollowLoading(false);
    }
  };

  // ── existing logic ────────────────────────────────────────────────────────
  const showingPrizes = prizes.filter(prize => userPrizes.some(item => item.odul_id === prize.id));
  const getTooltipText = () => {
    if (!user?.lig_temel_id || userData?.lig_temel_id !== user?.lig_temel_id) {
      return t('tooltips.differentLeague', { name: user.ad });
    }
    if (!userData.verified_account) {
      return NotificationText.ACTIVE_USER_NOT_VERIFIED_ACCOUNT;
    }
    if (!user.verified_account) {
      return NotificationText.USER_NOT_VERIFIED_ACCOUNT;
    }
  };

  const content = (
    <>
      {league ? <LeagueSummary user={user} league={league} nextMatch={findNextMatch(matches, user.id, users)} /> : ''}
      {showingPrizes?.length ? <Prizes showingPrizes={showingPrizes} /> : ''}
      <WeeklyAvailabilityGrid availability={availability?.musait_tablo} editable={false} />
    </>
  );

  const rightContent = (
    <>
      <UserInfo user={showContact && contact.data ? { ...user, ...contact.data } : user} showContact={showContact} />
      {showContact && contact.isError && <p role="alert">{t('contact.unavailable', 'İletişim bilgileri yüklenemedi.')} <Button type="link" onClick={() => contact.refetch()}>{t('contact.retry', 'Tekrar dene')}</Button></p>}
      <div className="list-detail-actions-buttons">
        <Tooltip title={getTooltipText}>
          <Button
            type="primary"
            block
            icon={<PlusCircleOutlined />}
            disabled={
              !user?.lig_temel_id ||
              userData?.lig_temel_id !== user?.lig_temel_id ||
              cannotOffers.includes(userData.id) ||
              !userData.verified_account ||
              !user.verified_account
            }
            onClick={() => router.push(`${ROUTE_PLAY}/${user?.id}`)}
          >
            {t('actions.proposeMatch')}
          </Button>
        </Tooltip>
      </div>
    </>
  );

  return (
    <>
      <BaseHead
        pageTitle={t('pageTitle.userProfile', { name: `${user.ad} ${user.soyad}` })}
        description={`${user.ad} ${user.soyad} tenis profili. ${user.galibiyet} galibiyet, ${user.maglubiyet} mağlubiyet${league?.il_ad ? `, ${league.il_ad} tenis ligi` : ''}. Maç geçmişi ve istatistikler. tenis.io`}
        noindex={!isProfileSearchIndexable(user)}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd({
              '@context': 'https://schema.org',
              '@type': 'ProfilePage',
              mainEntity: {
                '@type': 'Person',
                name: `${user.ad} ${user.soyad}`,
                ...(user.foto_url && { image: user.foto_url }),
                url: `https://tenis.io${convertTitleToUrl('profil', `${user.ad} ${user.soyad}`, user.id)}`,
                sport: 'Tennis',
                ...(league && {
                  memberOf: {
                    '@type': 'SportsOrganization',
                    name: `${league.lig_ad} Tenis Ligi`,
                    url: `https://tenis.io${convertTitleToUrl('ligler', league.lig_ad, league.id)}`,
                  },
                }),
                interactionStatistic: {
                  '@type': 'InteractionCounter',
                  interactionType: 'https://schema.org/FollowAction',
                  userInteractionCount: user.followers_count || 0,
                },
              },
            }),
          }}
        />
      </Head>

      <div className="container mt-xl mb-xl profile">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={6}>
            <div className="list-detail-left-wrapper">
              <div className="list-detail-image-wrapper">
                {(user?.foto_mini_url || user?.foto_mini_url === null) && (
                  <div className="list-detail-avatar-wrapper">
                    <Badge
                      dot={true}
                      color={user?.buhaftamusait_mi ? 'var(--tn-success)' : 'var(--tn-neutral)'}
                      offset={[-22, '90%']}
                      style={{ width: '20px', height: '20px' }}
                    >
                      <Avatar
                        src={storageImage(user?.foto_mini_url, 180)}
                        size={{ xs: 120, sm: 120, md: 140, lg: 180 }}
                        icon={<UserOutlined />}
                      />
                    </Badge>
                  </div>
                )}
              </div>
              <div className="list-detail-summary">
                <div className="list-detail-summary-title-wrapper">
                  <h1>
                    {user?.ad} {user?.soyad}
                  </h1>
                  <VerifiedAccount verifiedAccount={user?.verified_account} />
                </div>

                {/* ── Takip ve maç teklifi: ekranda tek primary — takip ediyorsan teklif, etmiyorsan takip ── */}
                {!isOwnProfile && userData && (
                  <div className="profile-follow-actions">
                    <Button
                      type={following ? 'default' : 'primary'}
                      size="small"
                      loading={followLoading}
                      onClick={handleFollow}
                      aria-pressed={following}
                    >
                      {following ? t('actions.followingState') : t('actions.follow')}
                    </Button>
                    {canProposeMatch && (
                      <Link href={`/oyna/${user.id}`}>
                        <a className={`ant-btn ant-btn-sm${following ? ' ant-btn-primary' : ''}`}>{t('actions.proposeMatch')}</a>
                      </Link>
                    )}
                  </div>
                )}

                {league?.il_ad ? (
                  <p>
                    <SvgPin className="mr-sm" />
                    {league?.il_ad}
                  </p>
                ) : (
                  ''
                )}
                <RecentForm matches={matches} userId={user.id} />
                <div className="list-detail-summary-switch-wrapper">
                  <span>{t('availability.status')}</span>
                  <Switch defaultChecked={user?.buhaftamusait_mi} disabled />
                </div>

                {/* ── Takipçi / Takip sayıları ── */}
                <div className="follow-stats">
                  <button type="button" onClick={openFollowingModal} className="follow-stats-item">
                    <strong>{user.following_count || 0}</strong> {t('following', 'Takip edilen')}
                  </button>
                  <span className="follow-stats-sep">·</span>
                  <button type="button" onClick={openFollowersModal} className="follow-stats-item">
                    <strong>{followerCount}</strong> {t('followers', 'Takipçi')}
                  </button>
                </div>
              </div>
            </div>
          </Col>
          <ListDetailMiddleColumn content={content} description={''} descriptionHtml={''} />
          <ListDetailRightColumn content={rightContent} sharePath={convertTitleToUrl('profil', `${user.ad} ${user.soyad}`, user.id)} shareTitle={`${user.ad} ${user.soyad}`} />
        </Row>

        <Tabs defaultActiveKey="1">
          <TabPane tab={<span>{t('tabs.matches')}</span>} key="1">
            {matches?.length ? (
              <Matches matches={matches} users={users} courts={courts} />
            ) : (
              <Alert message={EmptyText.MATCHES_NOT_FOUND} type="warning" showIcon />
            )}
          </TabPane>
          <TabPane tab={<span>{t('tabs.pyramid')}</span>} key="2">
            {user.piramit_sira_gecmis?.length ? (
              <div className="text-center">
                <h3>{t('pyramid.rankingTitle')}</h3>
                <LineChart history={user.piramit_sira_gecmis} />
              </div>
            ) : (
              <Result title={t('pyramid.userHistoryEmpty')} />
            )}
          </TabPane>
        </Tabs>
      </div>

      <FollowListModal
        kind="followers"
        userId={user.id}
        count={followerCount}
        visible={followersModalOpen}
        onClose={() => setFollowersModalOpen(false)}
      />
      <FollowListModal
        kind="following"
        userId={user.id}
        count={user.following_count || 0}
        visible={followingModalOpen}
        onClose={() => setFollowingModalOpen(false)}
      />
    </>
  );
}

export default Profil;
